import { Button, Input, Select } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../../app/store";
import AttachmentIcon from "../../../../../assets/icons/AttachmentIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import ReplyIcon from "../../../../../assets/icons/ReplyIcon";
import {
  useFileUploadMutation,
  useGetAllEmployeesQuery,
} from "../../../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useLazyGetUserByEmailQuery,
  useReplyMailMutation,
} from "../../../../../features/superAdmin/ticketService/ticketServiceApi";
import useDebounce from "../../../../../hooks/useDebounce";
import useError from "../../../../../hooks/useError";
import { toast } from "../../../../../lib/toast";

type Props = {
  replying: boolean;
  setReplying: (val: boolean) => void;
  replies?: any;
};

const ReplyEmail = ({ replying, setReplying, replies }: Props) => {
  const { id } = useParams();
  const user = useAppSelector((state) => state?.auth?.user);

  const [config, setConfig] = useState({ cc: false, bcc: false });
  const { keyword, handleInputSearch } = useDebounce();
  const [triggerGetUser] = useLazyGetUserByEmailQuery();
  const senderId = replies?.data?.[replies?.data?.length - 1]?.sender?.id;

  const { data: employeesResponse } = useGetAllEmployeesQuery({
    params: {
      page: 1,
      limit: 10000000,
    },
  });

  const [emails, setEmails] = useState([]);

  const [toValues, setToValues] = useState<string[]>([]);
  const [ccValues, setCCValues] = useState<string[]>([]);
  const [bccValues, setBCCValues] = useState<string[]>([]);

  // Component states
  const [attachments, setAttachments] = useState<string[]>([]);
  const [text, setText] = useState<string>("");

  // Mutations
  const [
    replyEmail,
    { isSuccess: isReplySuccess, isError: isReplyError, error: replyError },
  ] = useReplyMailMutation();
  const [fileUpload, { error: fileUpError, isError: isFileUpError }] =
    useFileUploadMutation();

  // Handler functions
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const maxFileSize = 30 * 1024 * 1024; // 30MB

    for (const file of files) {
      if (file.size > maxFileSize) {
        toast("error", `"${file.name}" exceeds max file size of 30MB.`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      try {
        const res: any = await fileUpload(formData).unwrap();
        if (res?.Location) {
          setAttachments((prev) => [...prev, res.Location]); // Add to list
        }
      } catch (err) {
        console.error(`Upload failed for ${file.name}`, err);
      }
    }

    e.target.value = "";
  };

  const handleSendReply = async () => {
    console.log(
      ccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: ccData } = await triggerGetUser(trimmedEmail);
        return ccData?.data[0]?.id;
      }),
    );

    const ccIdList = await Promise.all(
      ccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: ccData } = await triggerGetUser(trimmedEmail);
        return ccData?.data[0]?.id;
      }),
    );

    const bccIdList = await Promise.all(
      bccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: bccData } = await triggerGetUser(trimmedEmail);
        return bccData?.data[0]?.id;
      }),
    );
    const payload = {
      parentMailId: id,
      body: text,
      ccUserIds: ccIdList || [],
      bccUserIds: bccIdList || [],
      receiverId: String(toValues?.[0]),
      attachments: attachments,
    };

    replyEmail({ id: user?.id, data: payload });
  };

  const handleClear = useCallback(() => {
    setReplying(false);
    setText("");
    setAttachments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Errors
  useError(isReplyError, replyError, "Email reply sent failed.");
  useError(isFileUpError, fileUpError, "Attachment upload failed.");

  // Effects
  useEffect(() => {
    if (isReplySuccess) {
      handleClear();
      setToValues([]);
      setCCValues([]);
      setBCCValues([]);
      toast("success", "Reply mail sent");
    }
  }, [isReplySuccess, handleClear]);

  const handleSearchEmail = useCallback(async (value: string) => {
    const { data } = await triggerGetUser(value);
    const emails =
      data?.data?.map((user: any) => ({
        label: user.officialInfo?.email,
        value: user.officialInfo?.email,
      })) || [];

    setEmails(emails);
  }, []);

  useEffect(() => {
    if (keyword) handleSearchEmail(keyword);
  }, [keyword, handleSearchEmail]);
  useEffect(() => {
    if (senderId) {
      setToValues([senderId]);
    }
  }, [senderId]);

  if (!replying) {
    return (
      <div className="flex gap-2 pl-4 pr-4 pb-4">
        <Button
          className="flex items-center gap-2"
          onClick={() => setReplying(true)}
        >
          <span>
            <ReplyIcon />
          </span>
          <span>Reply</span>
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="w-full px-2">
        <Select
          mode="multiple"
          maxLength={1}
          placeholder="To"
          bordered={false}
          options={employeesResponse?.data?.employees?.map((emp) => ({
            label: emp?.officialInfo?.name,
            value: emp.id,
          }))}
          onSearch={handleInputSearch}
          value={toValues}
          onChange={(value) => {
            // Keep only the last selected value
            const latest = value.slice(-1);
            setToValues(latest);
          }}
          className="!w-full !border-none !shadow-none focus:!shadow-none focus:!ring-0"
          style={{
            width: 100,
            border: "none",
            boxShadow: "none",
          }}
          dropdownStyle={{
            borderRadius: 0,
          }}
        />
      </div>
      <div className="flex items-center justify-between mx-2">
        <div className="w-full">
          {config.cc && (
            <Select
              mode="tags"
              placeholder="CC"
              bordered={false}
              options={emails}
              onSearch={handleInputSearch}
              value={ccValues}
              onChange={(value) => setCCValues(value)}
              className="w-full !border-none !shadow-none focus:!shadow-none focus:!ring-0"
              style={{
                width: "100%",
                border: "none",
                boxShadow: "none",
              }}
              dropdownStyle={{
                borderRadius: 0,
              }}
            />
          )}
        </div>
        {config.cc || (
          <button
            onClick={() => setConfig({ ...config, cc: true })}
            className="block px-3 py-0.5"
          >
            CC
          </button>
        )}
      </div>
      <div className="flex items-center justify-between mx-2">
        <div className="w-full">
          {config.bcc && (
            <Select
              mode="tags"
              placeholder="BCC"
              bordered={false}
              options={emails}
              onSearch={handleInputSearch}
              value={bccValues}
              onChange={(value) => setBCCValues(value)}
              className="w-full !border-none !shadow-none focus:!shadow-none focus:!ring-0"
              style={{
                width: "100%",
                border: "none",
                boxShadow: "none",
              }}
              dropdownStyle={{
                borderRadius: 0,
              }}
            />
          )}
        </div>
        {config.bcc || (
          <button
            onClick={() => setConfig({ ...config, bcc: true })}
            className="block px-3 py-0.5"
          >
            BCC
          </button>
        )}
      </div>
      <div className="border border-slate-200 mt-2 mb-2 ml-2 mr-2 flex rounded-lg">
        <Input.TextArea
          className="border-none focus:shadow-none flex-1 resize-none !rounded-lg"
          style={{ resize: "none" }}
          placeholder=""
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      <ul className="space-y-1 p-2">
        {attachments.map((url, index) => {
          const fileName = decodeURIComponent(url.split("/").pop() || "");
          return (
            <li
              key={index}
              className="max-w-sm truncate text-sm text-blue-700"
              title={fileName}
            >
              {fileName}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between mt-2 mb-2 ml-2 mr-2">
        <div className="flex gap-4 items-center">
          <Button
            type="primary"
            className="w-[100px]"
            onClick={handleSendReply}
          >
            Send
          </Button>
          <label htmlFor="attachment" className="cursor-pointer">
            <input
              type="file"
              id="attachment"
              className="hidden"
              onChange={handleFileUpload}
              multiple
            />
            <AttachmentIcon />
          </label>
        </div>
        <Button onClick={handleClear}>
          <DeleteIcon />
        </Button>
      </div>
    </>
  );
};

export default ReplyEmail;
